import { Dialog, DialogSurface, makeStyles } from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { useState } from "react";

import { tokens } from "../../theme";

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================

const t = {
    // Spacing
    spacingVerticalXs: tokens.primitives["spacing-Vertical-XS"],
    spacingVerticalS: tokens.primitives["spacing-Vertical-S"],
    spacingVerticalM: tokens.primitives["spacing-Vertical-M"],
    spacingVerticalL: tokens.primitives["spacing-Vertical-L"],
    spacingHorizontalS: tokens.primitives["spacing-Horizontal-S"],
    spacingHorizontalM: tokens.primitives["spacing-Horizontal-M"],
    spacingHorizontalL: tokens.primitives["spacing-Horizontal-L"],
    spacingHorizontalXl: tokens.primitives["spacing-Horizontal-XL"],

    // Typography
    fontFamilyBase: tokens.primitives["font-Family-Base"],
    fontSizeBase300: tokens.primitives["font-Size-Base-300"],
    fontSizeBase400: tokens.primitives["font-Size-Base-400"],
    fontSizeBase600: tokens.primitives["font-Size-Base-600"],
    fontWeightRegular: tokens.primitives["font-Weight-Regular"],
    fontWeightMedium: tokens.primitives["font-Weight-Medium"],
    fontWeightSemiBold: tokens.primitives["font-Weight-SemiBold"],
    lineHeightBase300: tokens.primitives["lineHeight-Base-300"],
    lineHeightBase400: tokens.primitives["lineHeight-Base-400"],
    lineHeightBase500: tokens.primitives["lineHeight-Base-500"],

    // Colors
    themePrimary: tokens.palette["theme-Primary"],
    themeLighterAlt: tokens.palette["theme-LighterAlt"],
    themeLighter: tokens.palette["theme-Lighter"],
    neutralPrimary: tokens.semantic["neutral-Primary"],
    neutralSecondary: tokens.semantic["neutral-Secondary"],
    neutralTertiaryAlt: tokens.semantic["neutral-TertiaryAlt"],
    neutralLighterAlt: tokens.semantic["neutral-LighterAlt"],
    neutralLighter: tokens.semantic["neutral-Lighter"],
    dialogBackground: tokens.components.dialogBackground,
    inputBackgroundPressed: tokens.components.inputBackgroundPressed,

    // Radius
    radiusMed: tokens.primitives["stroke-Radius-Medium"],
    radiusLarge: tokens.primitives["stroke-Radius-Large"],
    radiusXXLarge: tokens.primitives["stroke-RadiusXxl"],

    // Shadows
    shadowFloatingMedium: tokens.shadows["shadow-Floating-Medium"],

    // Buttons
    btnPrimaryBg: tokens.buttons.buttonSolidPrimaryBackground,
    btnPrimaryFg: tokens.buttons.buttonSolidPrimaryForeground,
    btnPrimaryBgHover: tokens.buttons.buttonSolidPrimaryBackgroundHover,
    btnPrimaryBgPressed: tokens.buttons.buttonSolidPrimaryBackgroundPressed,
    btnOutlinedSecondaryBg: tokens.buttons.buttonOutlinedSecondaryBackground,
    btnOutlinedSecondaryBgHover: tokens.buttons.buttonOutlinedSecondaryBackgroundHover,
    btnOutlinedSecondaryBgPressed: tokens.buttons.buttonOutlinedSecondaryBackgroundPressed,
    btnOutlinedSecondaryStroke: tokens.buttons.buttonOutlinedSecondaryStroke,
    btnOutlinedSecondaryStrokeSelected: tokens.buttons.buttonOutlinedSecondaryStrokeSelected,
    btnOutlinedSecondaryFgSelected: tokens.buttons.buttonOutlinedSecondaryForegroundSelected,

    // Stroke
    strokeFocus: tokens.primitives.strokeFocus
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    dialogSurface: {
        maxWidth: "480px",
        width: "90vw",
        padding: 0,
        borderRadius: t.radiusXXLarge,
        boxShadow: t.shadowFloatingMedium,
        overflow: "hidden",
        backgroundColor: t.dialogBackground
    },

    dialogContent: {
        display: "flex",
        flexDirection: "column",
        padding: t.spacingHorizontalXl
    },

    dialogHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: t.spacingVerticalM
    },

    dialogTitle: {
        color: t.themePrimary,
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase600,
        fontWeight: t.fontWeightSemiBold as unknown as number,
        margin: 0,
        lineHeight: t.lineHeightBase500
    },

    closeButton: {
        background: "none",
        border: "none",
        padding: t.spacingVerticalXs,
        cursor: "pointer",
        color: t.neutralTertiaryAlt,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: t.radiusMed,
        transitionProperty: "color, background-color",
        transitionDuration: "0.15s",
        flexShrink: 0,

        "&:hover": {
            color: t.neutralSecondary,
            backgroundColor: t.neutralLighterAlt
        },

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        }
    },

    dialogBody: {
        display: "flex",
        flexDirection: "column",
        gap: t.spacingVerticalL
    },

    buttonGroup: {
        display: "flex",
        flexWrap: "wrap",
        gap: t.spacingHorizontalS
    },

    optionButton: {
        minWidth: "auto",
        padding: "6px 12px",
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightMedium as unknown as number,
        borderRadius: t.radiusLarge,
        backgroundColor: "transparent",
        color: t.neutralSecondary,
        border: `1px solid ${t.neutralLighter}`,
        cursor: "pointer",
        transitionProperty: "background-color, border-color, color",
        transitionDuration: "0.15s",

        "&:hover": {
            backgroundColor: t.btnOutlinedSecondaryBgHover
        },

        "&:active": {
            backgroundColor: t.btnOutlinedSecondaryBgPressed
        },

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        }
    },

    optionButtonChecked: {
        backgroundColor: t.themeLighterAlt,
        border: `1px solid ${t.btnOutlinedSecondaryStrokeSelected}`,
        color: t.btnOutlinedSecondaryFgSelected,

        "&:hover": {
            backgroundColor: t.themeLighter
        }
    },

    textareaContainer: {
        display: "flex",
        flexDirection: "column",
        width: "100%"
    },

    textarea: {
        width: "100%",
        boxSizing: "border-box",
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        lineHeight: t.lineHeightBase300,
        padding: t.spacingHorizontalM,
        borderRadius: t.radiusMed,
        border: `1px solid ${t.neutralLighter}`,
        backgroundColor: t.dialogBackground,
        color: t.neutralPrimary,
        resize: "none",
        transitionProperty: "border-color, background-color",
        transitionDuration: "0.15s",

        "&::placeholder": {
            color: t.neutralTertiaryAlt
        },

        "&:focus": {
            outline: "none",
            border: `1px solid ${t.btnOutlinedSecondaryStrokeSelected}`,
            backgroundColor: t.inputBackgroundPressed
        }
    },

    textareaHasContent: {
        backgroundColor: t.inputBackgroundPressed
    },

    charCount: {
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        color: t.neutralTertiaryAlt,
        textAlign: "right",
        marginTop: t.spacingVerticalXs
    },

    dialogActions: {
        display: "flex",
        justifyContent: "flex-end",
        gap: t.spacingHorizontalS,
        marginTop: t.spacingVerticalL
    },

    primaryButton: {
        padding: "6px 16px",
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightMedium as unknown as number,
        borderRadius: t.radiusLarge,
        backgroundColor: t.btnPrimaryBg,
        color: t.btnPrimaryFg,
        border: "none",
        cursor: "pointer",
        transitionProperty: "background-color",
        transitionDuration: "0.15s",

        "&:hover": {
            backgroundColor: t.btnPrimaryBgHover
        },

        "&:active": {
            backgroundColor: t.btnPrimaryBgPressed
        },

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        }
    },

    secondaryButton: {
        padding: "6px 16px",
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightMedium as unknown as number,
        borderRadius: t.radiusMed,
        backgroundColor: t.btnOutlinedSecondaryBg,
        color: t.neutralSecondary,
        border: `1px solid ${t.neutralLighter}`,
        cursor: "pointer",
        transitionProperty: "background-color, border-color",
        transitionDuration: "0.15s",

        "&:hover": {
            backgroundColor: t.btnOutlinedSecondaryBgHover
        },

        "&:active": {
            backgroundColor: t.btnOutlinedSecondaryBgPressed
        },

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        }
    }
});

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const UserAdditionalFeedback = (props: { feedbackSubmitted: (feedbackOptions: { option_id: number; option_text: string }[], userInput: string) => void }) => {
    const styles = useStyles();
    const [hideDialog, setHideDialog] = useState(false);
    const options = ["Not accurate", "Not relevant", "Not enough information", "Broken citation", "Other"];
    const [selectedOptions, setSelectedOptions] = useState<Set<number>>(new Set());
    const [userInput, setUserInput] = useState<string>("");

    const CHAR_LIMIT = 500;

    const onFeedbackBtnClicked = (optionId: number) => {
        setSelectedOptions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(optionId)) {
                newSet.delete(optionId);
            } else {
                newSet.add(optionId);
            }
            return newSet;
        });
    };

    const onUserInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        if (newValue.length <= CHAR_LIMIT) {
            setUserInput(newValue);
        }
    };

    const submitFeedback = () => {
        const feedbackOptions = Array.from(selectedOptions).map(id => ({
            option_id: id,
            // eslint-disable-next-line security/detect-object-injection -- Configuration object lookup with known key
            option_text: options[id]
        }));
        props.feedbackSubmitted(feedbackOptions, userInput);
        setHideDialog(true);
    };

    const closeDialog = () => {
        setHideDialog(true);
    };

    const hasContent = userInput.length > 0;

    return (
        <Dialog open={!hideDialog} onOpenChange={(_, data) => setHideDialog(!data.open)}>
            <DialogSurface className={styles.dialogSurface} aria-labelledby="feedback-title">
                <div className={styles.dialogContent}>
                    <div className={styles.dialogHeader}>
                        <h2 id="feedback-title" className={styles.dialogTitle}>
                            Provide additional feedback
                        </h2>
                        <button className={styles.closeButton} onClick={closeDialog} aria-label="Close dialog">
                            <Dismiss24Regular />
                        </button>
                    </div>

                    <div className={styles.dialogBody}>
                        <fieldset className={styles.buttonGroup} style={{ border: 'none', padding: 0, margin: 0 }}>
                            <legend className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>
                                Select feedback categories
                            </legend>
                            {options.map((item, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className={`${styles.optionButton} ${selectedOptions.has(index) ? styles.optionButtonChecked : ""}`}
                                    onClick={() => onFeedbackBtnClicked(index)}
                                    aria-pressed={selectedOptions.has(index)}
                                >
                                    {item}
                                </button>
                            ))}
                        </fieldset>

                        <div className={styles.textareaContainer}>
                            <label htmlFor="additional-feedback-textarea" className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>
                                Additional feedback details
                            </label>
                            <textarea
                                id="additional-feedback-textarea"
                                className={`${styles.textarea} ${hasContent ? styles.textareaHasContent : ""}`}
                                rows={5}
                                value={userInput}
                                onChange={onUserInputChange}
                                placeholder="(Optional) Feel free to add specific details"
                                aria-describedby="char-count-feedback"
                            />
                            <div id="char-count-feedback" className={styles.charCount} aria-live="polite" aria-atomic="true">
                                <span className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>
                                    Character count:
                                </span>
                                {userInput.length}/{CHAR_LIMIT}
                            </div>
                        </div>
                    </div>

                    <div className={styles.dialogActions}>
                        <button className={styles.secondaryButton} onClick={closeDialog}>
                            Cancel
                        </button>
                        <button className={styles.primaryButton} onClick={submitFeedback}>
                            Submit
                        </button>
                    </div>
                </div>
            </DialogSurface>
        </Dialog>
    );
};

export default UserAdditionalFeedback;
